import { useState } from 'react'
import { Input, Select, Textarea } from '../../components/forms'
import Button from '../../components/ui/Button'
import SubtaskList from './SubtaskList'
import LinkList from './LinkList'
import { formatDateInput } from '../../lib/format'

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
]

const RECURRENCE_OPTIONS = [
  { value: '', label: 'No Recurrence' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]

function TodoForm({
  initialData,
  businesses = [],
  users = [],
  onSubmit,
  onCancel,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onAddLink,
  onDeleteLink,
  loading = false,
}) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    priority: initialData?.priority || 'medium',
    status: initialData?.status || 'pending',
    business_id: initialData?.business_id || '',
    due_date: formatDateInput(initialData?.due_date || ''),
    tags: initialData?.tags?.join(', ') || '',
    assigned_to: initialData?.assigned_to || '',
    is_recurring: initialData?.is_recurring || false,
    recurrence_pattern: initialData?.recurrence_pattern || '',
    recurrence_end_date: formatDateInput(initialData?.recurrence_end_date || ''),
  })

  // Local state for subtasks and links (for new todos)
  const [localSubtasks, setLocalSubtasks] = useState([])
  const [localLinks, setLocalLinks] = useState([])

  // Use existing data for editing, local state for new todos
  const subtasks = initialData?.todo_subtasks || localSubtasks
  const links = initialData?.todo_links || localLinks
  const isEditing = !!initialData?.id

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      ...formData,
      business_id: formData.business_id || null,
      due_date: formData.due_date || null,
      assigned_to: formData.assigned_to || null,
      recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
      recurrence_end_date: formData.is_recurring && formData.recurrence_end_date ? formData.recurrence_end_date : null,
      tags: formData.tags
        ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [],
      // Include local subtasks/links for new todos
      _localSubtasks: isEditing ? undefined : localSubtasks,
      _localLinks: isEditing ? undefined : localLinks,
    })
  }

  // Handlers for local subtasks (new todos)
  const handleAddLocalSubtask = (title, assignedTo = null) => {
    setLocalSubtasks(prev => [...prev, {
      id: `temp-${Date.now()}`,
      title,
      is_completed: false,
      order_position: prev.length,
      assigned_to: assignedTo,
    }])
  }

  const handleToggleLocalSubtask = (id) => {
    setLocalSubtasks(prev => prev.map(s =>
      s.id === id ? { ...s, is_completed: !s.is_completed } : s
    ))
  }

  const handleDeleteLocalSubtask = (id) => {
    setLocalSubtasks(prev => prev.filter(s => s.id !== id))
  }

  // Handlers for local links (new todos)
  const handleAddLocalLink = (url, title) => {
    setLocalLinks(prev => [...prev, {
      id: `temp-${Date.now()}`,
      url,
      title,
    }])
  }

  const handleDeleteLocalLink = (id) => {
    setLocalLinks(prev => prev.filter(l => l.id !== id))
  }

  const businessOptions = businesses.map(b => ({
    value: b.id,
    label: `${b.emoji} ${b.name}`,
  }))

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.name || u.email,
  }))

  return (
    <form onSubmit={handleSubmit}>
      <Input
        label="Task Title"
        name="title"
        value={formData.title}
        onChange={handleChange}
        placeholder="What needs to be done?"
        required
      />

      <Textarea
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleChange}
        placeholder="Additional details..."
        rows={2}
      />

      <div className="grid grid-2">
        <Select
          label="Priority"
          name="priority"
          value={formData.priority}
          onChange={handleChange}
          options={PRIORITY_OPTIONS}
        />
        <Select
          label="Status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          options={STATUS_OPTIONS}
        />
      </div>

      <div className="grid grid-2">
        <Select
          label="Business"
          name="business_id"
          value={formData.business_id}
          onChange={handleChange}
          options={businessOptions}
          placeholder="Select business..."
        />
        <Input
          label="Due Date"
          name="due_date"
          type="date"
          value={formData.due_date}
          onChange={handleChange}
        />
      </div>

      {users.length > 0 && (
        <Select
          label="Assign To"
          name="assigned_to"
          value={formData.assigned_to}
          onChange={handleChange}
          options={userOptions}
          placeholder="Unassigned"
        />
      )}

      <Input
        label="Tags"
        name="tags"
        value={formData.tags}
        onChange={handleChange}
        placeholder="marketing, urgent, followup (comma separated)"
      />

      {/* Recurring Task Section */}
      <div className="form-group mt-4">
        <label className="checkbox-item">
          <input
            type="checkbox"
            name="is_recurring"
            checked={formData.is_recurring}
            onChange={handleChange}
          />
          <span className="checkbox-label">Recurring Task</span>
        </label>
      </div>

      {formData.is_recurring && (
        <div className="grid grid-2 mt-2">
          <Select
            label="Repeat"
            name="recurrence_pattern"
            value={formData.recurrence_pattern}
            onChange={handleChange}
            options={RECURRENCE_OPTIONS.filter(o => o.value)}
          />
          <Input
            label="End Recurrence"
            name="recurrence_end_date"
            type="date"
            value={formData.recurrence_end_date}
            onChange={handleChange}
            placeholder="Optional"
          />
        </div>
      )}

      {/* Subtasks Section */}
      <div className="form-group mt-4">
        <label className="form-label">Subtasks</label>
        <div
          style={{
            padding: '0.75rem',
            background: 'var(--gray-50)',
            borderRadius: '0.375rem',
            border: '1px solid var(--gray-200)',
          }}
        >
          <SubtaskList
            subtasks={subtasks}
            users={users}
            onToggle={isEditing ? onToggleSubtask : handleToggleLocalSubtask}
            onAdd={isEditing ? onAddSubtask : handleAddLocalSubtask}
            onDelete={isEditing ? onDeleteSubtask : handleDeleteLocalSubtask}
            editable={true}
            loading={loading}
          />
        </div>
      </div>

      {/* Links Section */}
      <div className="form-group mt-4">
        <label className="form-label">Links</label>
        <div
          style={{
            padding: '0.75rem',
            background: 'var(--gray-50)',
            borderRadius: '0.375rem',
            border: '1px solid var(--gray-200)',
          }}
        >
          <LinkList
            links={links}
            onAdd={isEditing ? onAddLink : handleAddLocalLink}
            onDelete={isEditing ? onDeleteLink : handleDeleteLocalLink}
            editable={true}
            loading={loading}
          />
        </div>
      </div>

      <div className="flex justify-between gap-3 mt-6">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {initialData ? 'Update' : 'Add'} Task
        </Button>
      </div>
    </form>
  )
}

export default TodoForm
